import { usePageMeta } from '@/hooks/usePageMeta';
import PageShell, { PageHero } from '@/components/PageShell';

const PRIVACY_DESCRIPTION =
  "Sport Fund privacy policy — how we collect, use, and protect your information when you use our athlete-fundraising platform.";

export default function Privacy() {
  usePageMeta('Privacy', PRIVACY_DESCRIPTION);
  return (
    <PageShell
      hero={
        <PageHero
          eyebrow="Privacy policy"
          title="How we handle your data."
          lead="The short version: we collect the minimum needed to run the platform, never sell your data, and explain everything in plain language below."
          size="sm"
        />
      }
    >
      <section className="bg-white">
        <article className="mx-auto max-w-3xl px-6 py-20 md:py-28 prose prose-slate prose-lg prose-headings:font-extrabold prose-headings:tracking-tight prose-h2:text-3xl prose-h2:mt-12 prose-h2:mb-4 prose-p:leading-relaxed prose-a:text-blue-700 prose-a:font-medium hover:prose-a:text-blue-800 prose-strong:text-slate-900">
          <p className="text-sm text-slate-500">
            <em>Sport Fund is operated by BackerBase LLC, a Wyoming limited liability company, 1621 Central Avenue, Cheyenne, WY 82001, USA.</em>
            <br />
            <strong>Last updated:</strong> 19 September 2026
          </p>
          <h2>Introduction</h2>
          <p>
            Sport Fund ("we," "our," or "us") values your privacy and is committed to protecting your personal information. This Privacy Policy explains how we collect, use, and share information about you when you use our website and services.
          </p>
          <h2>1. Information we collect</h2>
          <ul>
            <li><strong>Personal information.</strong> When you create a profile or contribute through our platform, we collect information such as your name, email address and payment details.</li>
            <li><strong>Usage information.</strong> We collect information about your interactions with our website, including the pages you visit, the features you use, and the actions you take.</li>
            <li><strong>Device information.</strong> We may collect information about the device you use, including IP address, browser type, operating system and device identifiers.</li>
          </ul>
          <h2>2. How we use your information</h2>
          <ul>
            <li>To provide, maintain, and improve our services.</li>
            <li>To process transactions and send related information such as confirmations and receipts.</li>
            <li>To communicate with you about your account, fundraising activity, and platform updates.</li>
            <li>To personalize your experience.</li>
            <li>To monitor and analyze trends, usage, and activity in connection with our services.</li>
          </ul>
          <h2>3. Analytics and reach measurement</h2>
          <p>
            <strong>Product analytics &amp; reach measurement — PostHog</strong> (EU-hosted, Frankfurt; eu.i.posthog.com). We use a two-tier approach:
          </p>
          <ul>
            <li>
              <strong>(1) Basic reach measurement.</strong> An anonymous, cookie-free measurement of page views and visitor counts runs <strong>without consent</strong> — nothing is stored on or read from your device, so §25 TTDSG does not apply; legal basis Art. 6(1)(f) GDPR (our legitimate interest in privacy-minimising reach measurement).
            </li>
            <li>
              <strong>(2) Extended analysis.</strong> Persistent cookies for cross-session insights and session replay run <strong>only after your explicit consent</strong> via the banner (&ldquo;Accept&rdquo;); legal basis Art. 6(1)(a) GDPR in conjunction with §25(1) TTDSG. &ldquo;Decline&rdquo; disables all analytics.
            </li>
          </ul>
          <p>
            A data-processing agreement and EU hosting are in place; inputs to password fields are masked in session replay.
          </p>
          <h2>4. Sharing your information</h2>
          <p>We do not sell or trade your personal information. We only share it in these specific circumstances:</p>
          <ul>
            <li><strong>Service providers</strong> who help us operate the platform under confidentiality agreements (e.g. Stripe for payments).</li>
            <li><strong>Legal compliance</strong> when required by law, or to protect our rights, property, or safety, or that of our users.</li>
          </ul>
          <h2>5. Your rights and choices</h2>
          <ul>
            <li><strong>Access and correction.</strong> You can access and update your personal information at any time by logging in.</li>
            <li><strong>Opt out.</strong> Unsubscribe from promotional emails using the link in those emails or by contacting us directly.</li>
            <li><strong>Cookies.</strong> A necessary cookie <code>sportfund_consent</code> stores your analytics choice. PostHog analytics cookies are set only after you click &ldquo;Accept&rdquo; on the consent banner; basic reach measurement is cookie-free. You can also configure your browser to refuse cookies, though some features may not work properly without them.</li>
          </ul>
          <h2>6. Data security</h2>
          <p>
            We implement industry-standard security measures to protect your personal information. No method of transmission over the internet or electronic storage is 100% secure, so we cannot guarantee absolute security.
          </p>
          <h2>7. Changes to this privacy policy</h2>
          <p>
            We may update this Privacy Policy from time to time. We'll post the new policy on our website and update the "Last updated" date above.
          </p>
          <h2>8. Contact us</h2>
          <p>
            Questions about this policy or our data practices? Email us at <a href="mailto:info@thesportfund.com">info@thesportfund.com</a>.
          </p>
        </article>
      </section>
    </PageShell>
  );
}
